import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Tabs from '@cloudscape-design/components/tabs';
import Box from '@cloudscape-design/components/box';
import { useAppSelector } from 'src/store/hooks';
import { withRegion } from 'src/constants';
import { SearchForm } from './SearchForm';
import './search.css';

type SearchTab = 'payment' | 'artifact' | 'execution';

interface TabConfig {
  id: SearchTab;
  label: string;
  placeholder: string;
  /** Omitted when the entity has no bulk search, which hides the toggle. */
  bulkPlaceholder?: string;
}

const tabConfigs: TabConfig[] = [
  {
    id: 'payment',
    label: 'Payment identifiers',
    placeholder: 'Enter End-to-End ID, e.g. DASAVFAR7Y9W2DC',
    // paymentIdentifiers matches correlation tags, so any of the three resolve.
    bulkPlaceholder: 'Enter multiple payment, artifact or execution IDs, one per line',
  },
  // No bulk for these two: the payment tab already matches artifact and
  // execution ids through correlation tags, so a bulk list of either is
  // answered there.
  {
    id: 'artifact',
    label: 'Artifact identifiers',
    placeholder: 'Enter artifact file name, e.g. pain.001.001.03_2025-06-01.xml',
  },
  {
    id: 'execution',
    label: 'Execution identifiers',
    placeholder: 'Enter execution name',
  },
];

/** The entity the current route is about. */
const tabForPath = (pathname: string): SearchTab => {
  if (pathname.startsWith('/artifact')) return 'artifact';
  if (pathname.startsWith('/execution')) return 'execution';
  return 'payment';
};

export const SearchTabs = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const viewedId = searchParams.get('id') ?? '';
  const region = useAppSelector(state => state.env.region);
  const [activeTab, setActiveTab] = useState<SearchTab>(() => tabForPath(pathname));
  const [bulkMode, setBulkMode] = useState(false);
  const [searchValue, setSearchValue] = useState(viewedId);

  /** A hand-picked tab starts empty; the field is per-entity. */
  const selectTab = (tab: SearchTab) => {
    setActiveTab(tab);
    setSearchValue('');
    setBulkMode(false);
  };

  // Mirror the route, so a detail page shows its own tab and id. Keyed on the
  // route alone: switching tabs or typing must not re-run it.
  useEffect(() => {
    setActiveTab(tabForPath(pathname));
    setSearchValue(viewedId);
  }, [pathname, viewedId]);

  const handleSearch = () => {
    const trimmed = searchValue.trim();
    if (!trimmed) return;

    if (bulkMode) {
      // Only the payment tab offers bulk, and its search matches artifact and
      // execution ids through correlation tags, so every bulk list lands there.
      const ids = trimmed.split('\n').map(line => line.trim()).filter(Boolean);
      if (ids.length === 0) return;
      navigate(withRegion(`/payments?ids=${ids.map(encodeURIComponent).join(',')}`, region));
      return;
    }

    // Single-identifier search → detail page.
    const route = { payment: '/payment', artifact: '/artifact', execution: '/execution' }[activeTab];
    navigate(withRegion(`${route}?id=${encodeURIComponent(trimmed)}`, region));
  };

  return (
    <div className="search-tabs">
      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => selectTab(detail.activeTabId as SearchTab)}
        tabs={tabConfigs.map(config => ({
          id: config.id,
          label: config.label,
          content: (
            <Box padding={{ top: 'm' }}>
              <SearchForm
                placeholder={config.placeholder}
                bulkPlaceholder={config.bulkPlaceholder}
                bulkMode={bulkMode}
                onBulkModeChange={setBulkMode}
                searchValue={searchValue}
                onSearchValueChange={setSearchValue}
                onSearch={handleSearch}
              />
            </Box>
          ),
        }))}
      />
    </div>
  );
};
